<template>
  <div class="tag-select">
    <h3 v-if="title"
        v-html="title"></h3>
    <button class="add-tag"
            :disabled="adding"
            @click="addTagName">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <g fill-rule="evenodd" transform="translate(2 2)">
          <rect width="12" height="2" y="5" rx="1" />
          <rect width="12" height="2" y="5" rx="1" transform="rotate(90 6 6)" />
        </g>
      </svg>
      {{ $t('group.editMode.addTag') }}
    </button>

    <div class="pagination">
      <ui-navigation class="tag-navigation"
                     :currentPage="listOption.pageNum"
                     :lastPage="lastPage"
                     @setPage="setPage" />
      <span class="view-per-page">
          {{ $t('common.pagination.viewPerPage') }} : {{ listOption.itemsPerPage }}
        </span>
    </div>

    <div class="tags"
         ref="tagSelect"
         :class="{ adding }">
      <ul>
        <li v-if="adding"
            class="adding"
            :class="{ error: editingError }">
          <span class="bullet"
                :style="{ 'background-color': nextColor.colorHex }"></span>
          <input type="text"
                 v-model="newTagName"
                 id="addTagInput"
                 :placeholder="$t('group.editMode.insertTagName')"
                 @blur="addTag(true)"
                 @keyup="editingError = ''"
                 @keyup.esc="addTag(tag)"
                 @keyup.enter="$event.target.blur()" />
          <i class="icon icon--check-white-size16"></i>
          <ui-icon-tooltip v-if="editingError"
                           :iconSize="'red'"
                           arrowBottom
                           arrowCenter>
            <span v-if="editingError === 'duplicate'"
                  v-html="$t('group.editMode.duplicatedTagName')"></span>
          </ui-icon-tooltip>
        </li>
        <template v-for="(item, idx) in listData">
          <li
              v-if="!adding || idx < 5"
              :key="`tag-item-${item.tagId}`"
              :class="{ active: tag && tag.tagId === item.tagId }"
              @click="setTag(item)">
          <span class="bullet"
                :style="{ 'background-color': item.colorHex }"></span>
            {{ item.tagName }}
            <i class="icon icon--check-white-size16"></i>
          </li>
        </template>

      </ul>
    </div>

    <div class="buttons">
      <ui-button v-if="cancel"
                 @click="$emit('onCancel')">
        <span v-html="cancel"></span>
      </ui-button>
      <ui-button v-if="submit"
                 :primary2="true"
                 :disabled="!tag"
                 @click="$emit('onSubmit', tag)">
        <span v-html="submit"></span>
      </ui-button>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/libs/event-bus'

import { getTagList, upsertTag, checkTagDuplication } from '@/api/group'

import UiButton from '@/components/ui/UiButton'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiNavigation from '@/components/ui/UiNavigation'

export default {
  name: 'TagSelect',
  components: {
    UiButton,
    UiIconTooltip,
    UiNavigation
  },
  props: {
    count: {},
    group: {},
    colorList: {},
    excludeTagId: {
      type: Number
    },
    title: {
      type: String
    },
    cancel: {
      type: String
    },
    submit: {
      type: String
    }
  },
  computed: {
    sid () {
      return this.$route.query.sid
    }
  },
  created () {
    if (this.group) {
      this.loadTagList()
    }
    this.calcLastPage()
  },
  methods: {
    setTag (tag) {
      if (!this.adding) {
        this.tag = tag
      }
    },

    setPage (page) {
      this.listOption.pageNum = page
      this.loadTagList().then(() => {
        this.$refs.tagSelect.scrollTop = 0
      })
    },

    loadTagList (force = true) {
      EventBus.$emit('onLoading', true)
      return new Promise(resolve => {
        getTagList({
          sid: this.sid,
          pageId: this.listOption.pageNum - 1,
          pageNum: this.listOption.itemsPerPage,
          sort: this.listOption.sort,
          groupId: this.group.groupId
        }, force).then(result => {
          this.listData = []
          for (let tag of result) {
            if (tag.tagId > -1 && tag.tagId !== this.excludeTagId) {
              this.listData.push({
                tagId: tag.tagId,
                colorHex: tag.colorHex,
                tagName: tag.tagName,
                tagNameEditing: tag.tagName,
                numOfUrl: tag.numOfUrl,
                recentUrlCount: tag.recentUrlCount || 0
              })
            }

            const orderField = 'tagName'
            this.listData.sort((a, b) => {
              if (a.tagId < 0) {
                return -1
              }
              if (b.tagId < 0) {
                return 1
              }
              if (typeof a[orderField] === 'string' && typeof b[orderField] === 'string') {
                return new Intl.Collator('en').compare(a[orderField].toString(), b[orderField].toString())
              }
            })
          }
          this.calcLastPage()
          EventBus.$emit('onLoading', false)
          resolve()
        })
      })
    },

    setNextColor () {
      return new Promise(resolve => {
        let colors = [...this.colorList]
        let usingColors = this.listData.map(item => item.colorHex)
        let usingColorCount = {}
        for (const color of colors) {
          usingColorCount[color.colorHex] = usingColors.filter(item => item === color.colorHex).length
        }
        let minimumCount = Object.values(usingColorCount).reduce((min, item) => Math.min(min, item), Number.POSITIVE_INFINITY)

        for (const [colorHex, count] of Object.entries(usingColorCount)) {
          if (count === minimumCount) {
            this.nextColor = colors.find(item => item.colorHex === colorHex)
            break
          }
        }
        resolve()
      })
    },

    calcLastPage () {
      if (this.count && typeof this.count.tag === 'number') {
        this.lastPage = Math.ceil(this.count.tag / this.listOption.itemsPerPage)
      } else {
        this.lastPage = 0
      }
    },

    checkTagNameDuplication (tagName) {
      return new Promise((resolve, reject) => {
        if (['미태그', 'Untagged', 'タグ無し'].includes(tagName)) {
          reject(new Error('untagged'))
        } else {
          checkTagDuplication({
            sid: this.sid,
            groupId: this.group.groupId,
            tagName
          }, true).then(result => {
            if (result.duplicate) {
              reject(new Error('duplicate'))
            } else {
              resolve()
            }
          })
        }
      })
    },

    addTagName () {
      this.setNextColor().then(() => {
        this.adding = true
        this.tag = null
        setTimeout(() => {
          document.getElementById('addTagInput').focus()
        }, 0)
      })
    },

    addTag (submit = false) {
      if (!this.newTagName) {
        submit = false
      }
      if (!submit) {
        this.newTagName = ''
        this.adding = false
      } else if (this.newTagName) {
        EventBus.$emit('onLoading', true)
        this.checkTagNameDuplication(this.newTagName).then(() => {
          this.adding = false
          upsertTag({
            sid: this.sid,
            groupId: this.group.groupId,
            tagName: this.newTagName,
            colorId: this.nextColor.colorId,
            colorHex: this.nextColor.colorHex
          }).then(result => {
            this.loadTagList(true).then(() => {
              this.setTag(result)
            })
            this.newTagName = ''
            EventBus.$emit('reloadGroupStat')
            EventBus.$emit('reloadTagList')
            EventBus.$emit('onLoading', false)
          })
        }).catch(() => {
          document.getElementById('addTagInput').focus()
          this.editingError = 'duplicate'
          EventBus.$emit('onLoading', false)
        })
      }
    }
  },
  data () {
    return {
      listOption: {
        pageNum: 1,
        itemsPerPage: 50,
        sort: 'tagName'
      },
      lastPage: 0,
      tag: null,
      listData: [],
      adding: false,
      newTagName: '',
      editingError: '',
      nextColor: {}
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-select {
  position: relative;
  width: 500px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #fff;
  text-align: center;

  h3 {
    padding: 30px 30px 0 30px;
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    color: #2f2f2f;
  }

  .add-tag {
    position: absolute;
    top: 25px;
    right: 30px;
    padding: 5px;
    font-size: 13px;
    line-height: 1;
    color: #444;
    transition: opacity 0.15s ease,
                color 0.15s ease;

    svg {
      vertical-align: middle;
      margin-top: -2px;
      margin-right: 2px;

      g {
        fill: #666666;
        transition: fill 0.15s ease;
      }
    }

    &:not(:disabled):hover {
      color: #ec0047;

      svg {
        g {
          fill: #ec0047;
        }
      }
    }

    &:disabled {
      opacity: 0.3;
    }
  }

  .pagination {
    padding: 10px 15px 0 22px;

    .tag-navigation {
      float: left;
    }

    .view-per-page {
      float: right;
      font-size: 12px;
      color: #767676;
      padding: 19px 15px 20px 0;
    }
  }

  .tags {
    clear: both;
    margin: 0 30px;
    border-radius: 5px;
    border: solid 1px #d7d7d7;
    background-color: #f4f4f4;
    padding: 9px 10px 17px;
    overflow: auto;
    height: 212px;
    text-align: left;

    &.adding {
      overflow: visible;
    }

    ul {
      padding: 6px 0;

      li {
        position: relative;
        cursor: pointer;
        line-height: 38px;
        height: 38px;
        margin: 0 3px;
        border-radius: 36px;
        transition: background-color 0.15s ease,
                    color 0.15s ease;
        font-size: 13px;
        color: #2f2f2f;
        padding: 0 30px 0 31px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:hover {
          background-color: #e0e0e0;
        }

        &.active {
          color: #ffffff;
          background-color: #289eef;

          .bullet {
            border-color: #ffffff;
          }

          .icon {
            opacity: 1;
          }
        }

        .bullet {
          position: absolute;
          top: 12.5px;
          left: 11px;
          display: inline-block;
          width: 10px;
          height: 10px;
          border: solid 1px rgba(0, 0, 0, 0.1);
          background-color: #bbbbbb;
          border-radius: 12px;
          vertical-align: middle;
          transition: border-color 0.15s ease;
        }

        .icon {
          opacity: 0;
          position: absolute;
          top: 11px;
          right: 16px;
          transition: opacity 0.15s ease;
        }

        &.adding {
          overflow: visible;
          background-color: #289eef;

          &.error {
            background-color: #e60724;

            input {
              border-color: #e60724;
            }
          }

          input {
            width: 320px;
            font-size: 14px;
            line-height: 30px;
            margin-top: 3px;
            padding: 0 9px;
            color: #2f2f2f;
            border-radius: 4px;
            border: solid 1px #289eef;
            background-color: #fff;
            transition: border-color 0.15s ease;
          }

          .bullet {
            border-color: #ffffff;
          }

          .icon {
            opacity: 1;
          }

          & /deep/ .tooltip {
            .icon {
              position: absolute;
              top: 11px;
              right: 7px;
            }

            .bubble {
              bottom: -3px !important;
              margin-left: -15px !important;
              display: block !important;
              opacity: 1 !important;
              padding-top: 7px;
              padding-bottom: 7px;

              .text {
                font-weight: normal;
                color: #ec0047;
              }
            }
          }
        }
      }
    }
  }

  .buttons {
    padding: 15px 20px 13px;

    .ui-btn {
      min-width: 90px;
      margin: 0 2px 0 3px;
    }
  }
}
</style>
